import { PROGRAM_TYPES } from 'utils/program/helpers'
import { doesLessonNodeMatch } from 'utils/lms/lessonNodeHelpers'

/**
 * Unified function to check if a resource is complete based on instructor data
 * Handles both new and legacy data structures with comprehensive matching strategies
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.instructorData - The instructor data from Redux
 * @param {string} params.courseNodeId - The course node ID to check
 * @param {string} params.program - The current program type
 * @param {string} [params.registrationId] - The registration ID from URL query params
 * @param {boolean} [params.allowAnyRegistrationMatch] - Read-only fallback for
 * classless views that still need completion status; completion writes still
 * require a concrete registrationId elsewhere
 * @returns {boolean} - Whether the resource is complete
 */
export const isResourceComplete = ({
  instructorData,
  courseNodeId,
  program,
  registrationId = null,
  allowAnyRegistrationMatch = false,
}) => {
  if (!instructorData || !courseNodeId) {
    return false
  }

  // Check if instructorData has the new structure with educator and siteEducator arrays
  if (instructorData.educator || instructorData.siteEducator) {
    return checkNewStructure({
      instructorData,
      courseNodeId,
      registrationId,
      allowAnyRegistrationMatch,
    })
  }

  // Legacy structure: check program-specific data
  return checkLegacyStructure({
    instructorData,
    courseNodeId,
    program,
  })
}

/**
 * Check completion status using the new data structure
 * @param {Object} params - Parameters object
 * @param {Object} params.instructorData - The instructor data
 * @param {string} params.courseNodeId - The course node ID to check
 * @param {string} params.registrationId - The registration ID
 * @returns {boolean} - Whether the resource is complete
 */
const checkNewStructure = ({
  instructorData,
  courseNodeId,
  registrationId,
  allowAnyRegistrationMatch,
}) => {
  const hasMatchingLessonNode = lesson =>
    doesLessonNodeMatch(lesson, courseNodeId)

  const allCourseInstances = [
    ...(Array.isArray(instructorData.siteEducator)
      ? instructorData.siteEducator
      : []),
    ...(Array.isArray(instructorData.educator) ? instructorData.educator : []),
  ]

  // Default path: require registrationId so we scope completion to a single
  // class instance. The allowAnyRegistrationMatch override is only used for
  // read-only completion display in classless routes; mark-complete writes are
  // handled by separate logic that always requires registrationId.
  if (!registrationId && !allowAnyRegistrationMatch) {
    return false
  }

  const matchingCourseInstances =
    registrationId && !allowAnyRegistrationMatch
      ? allCourseInstances.filter(
          course => String(course.registration_id) === String(registrationId),
        )
      : allCourseInstances

  if (!matchingCourseInstances.length) {
    return false
  }

  return matchingCourseInstances.some(courseInstance => {
    if (!Array.isArray(courseInstance?.lesson_nodes)) {
      return false
    }
    const lessonNode = courseInstance.lesson_nodes.find(hasMatchingLessonNode)
    return lessonNode?.is_done || false
  })
}

/**
 * Check completion status using the legacy data structure
 * @param {Object} params - Parameters object
 * @param {Object} params.instructorData - The instructor data
 * @param {string} params.courseNodeId - The course node ID to check
 * @param {string} params.program - The current program type
 * @returns {boolean} - Whether the resource is complete
 */
const checkLegacyStructure = ({ instructorData, courseNodeId, program }) => {
  // For tiered interventions, we need to check the appropriate instructor data
  let relevantInstructorData = null
  if (
    program === PROGRAM_TYPES.ELEMENTARY ||
    program === PROGRAM_TYPES.ELEMENTARY_TIER_2
  ) {
    relevantInstructorData = instructorData?.K5SelDigital
  } else if (
    program === PROGRAM_TYPES.MIDDLE_SCHOOL ||
    program === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2
  ) {
    relevantInstructorData = instructorData?.MspDigital
  }

  const educatorEntries = Array.isArray(relevantInstructorData?.educator)
    ? relevantInstructorData.educator
    : relevantInstructorData?.educator
    ? [relevantInstructorData.educator]
    : []

  if (!educatorEntries.length) {
    return false
  }

  return educatorEntries.some(educatorEntry => {
    if (!Array.isArray(educatorEntry?.lesson_nodes)) {
      return false
    }
    const lessonNode = educatorEntry.lesson_nodes.find(
      lesson => lesson.course_node_id === courseNodeId,
    )
    return lessonNode?.is_done || false
  })
}
